import type React from 'react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { govLog, useAppContext } from '@gov-nx/core/app';
import {
	getI18n,
	i18nLoadNamespaces,
	LocalizeNameSpaceTypes,
	useCmsService,
	useCmsServiceQuery,
	useCountryLoader,
	useLanguageLoader,
} from '@gov-nx/core/service';
import type { ServiceCode } from '@gov-nx/module/service';

export interface CmsServiceProviderCoreProps {
	children: React.ReactNode;
	code: ServiceCode;
	onError?: () => void;
	onNotFound?: () => void;
	skeleton?: React.ReactNode;
	loadExtraTranslations?: ServiceCode[];
}

export const CmsServiceProviderCore = forwardRef(
	(
		{
			children,
			code,
			skeleton = null,
			loadExtraTranslations = [],
		}: CmsServiceProviderCoreProps,
		ref
	) => {
		const [translationLoaded, setTranslationLoaded] = useState(
			getI18n().hasLoadedNamespace(code)
		);
		const { setCurrentCode } = useAppContext();
		const service = useCmsService({ code });
		const { loadCountries } = useCountryLoader();
		const { loadLanguages } = useLanguageLoader();

		const serviceQuery = useCmsServiceQuery({ code });

		const init = useCallback(async () => {
			await Promise.all([
				i18nLoadNamespaces([
					...loadExtraTranslations,
					code,
					LocalizeNameSpaceTypes.Form,
					LocalizeNameSpaceTypes.Service,
				]),
				serviceQuery.refetch(),
				loadCountries(),
				loadLanguages(),
			]);
			setTranslationLoaded(getI18n().hasLoadedNamespace(code));
			// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		}, [code, serviceQuery, loadCountries, loadLanguages]);

		useImperativeHandle(ref, () => ({
			init() {
				setCurrentCode(null, code);
				init().finally(() => {
					govLog('Page loaded [' + code + ']');
				});
			},
		}));

		if (service && translationLoaded) {
			return children;
		}

		return skeleton;
	}
);
